import type { MapFactory } from '../map-factory.model';
import uaeFactoriesSample from './uae-factories.sample.json';

type SampleFactory = {
  id: number;
  industrial_area_id: number;
  latitude: number;
  longitude: number;
  name: string;
  slug: string;
};

/**
 * Leaf capability ids from {@link import('@indutrust/shared-model').MOCK_TECHNOLOGIES_DEMO}.
 * Used to seed map pins so demo `q[capability_ids]` filtering can match.
 */
export const DEMO_MAP_CAPABILITY_LEAF_IDS = [
  11, 12, 13, 21, 22, 23, 31, 32, 33, 41, 42, 43,
] as const;

/**
 * Deterministic capability leaf ids for a map factory pin.
 * Factory `1` keeps the published showcase pair `[11, 12]`.
 */
export function seedMapFactoryCapabilityIds(factoryId: number): number[] {
  if (factoryId === 1) {
    return [11, 12];
  }

  const leafCount = DEMO_MAP_CAPABILITY_LEAF_IDS.length;
  const primary =
    DEMO_MAP_CAPABILITY_LEAF_IDS[(factoryId - 1) % leafCount] ?? 11;
  const secondary =
    DEMO_MAP_CAPABILITY_LEAF_IDS[(factoryId + 2) % leafCount] ?? 12;

  return primary === secondary ? [primary] : [primary, secondary];
}

/**
 * Builds {@link MapFactory} demo pins from the adapted UAE sample JSON.
 *
 * Factory `1` keeps `company_id: 1` so `/map?company=1` deep links resolve.
 * Other pins use `company_id = factory.id` (samples have no company field).
 * Verification and capability_ids are deterministic demo data — not claims
 * about the real plant.
 */
export function buildUaeMapFactoriesDemo(): MapFactory[] {
  const factories = uaeFactoriesSample as SampleFactory[];

  return factories.map((factory) => ({
    id: factory.id,
    name: factory.name,
    industrial_area_id: factory.industrial_area_id,
    latitude: factory.latitude,
    longitude: factory.longitude,
    company_id: factory.id === 1 ? 1 : factory.id,
    verified: factory.id === 1 ? true : factory.id % 3 !== 0,
    capability_ids: seedMapFactoryCapabilityIds(factory.id),
  }));
}
